import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { formatPrice } from "@/Helpers/formatPrice";
import { formatSignedPrice } from "@/Helpers/formatSignedPrice";

interface CreditBannerMessageProps {
  paymentTermsDays: number | undefined;
  creditAmount: number | undefined;
  creditCurrency: Currency | undefined;
  issuedInvoicesTotal: number | undefined;
  /**
   * The currency the invoice totals are actually in, read off the invoices themselves.
   *
   * Not creditCurrency: that is the company's current billing currency, which the credit limit is
   * denominated in but the invoices are not. An invoice keeps the currency it was created with, so
   * a company switched to USD still has CAD invoices, and labelling their total "USD" was wrong.
   * Undefined when the invoices mix currencies, in which case the code is left off.
   */
  invoicesCurrency: string | undefined;
  /** Only the booking-suspended banner names the overdue balance. */
  overdueIssuedInvoicesTotal?: number | undefined;
  /**
   * Set by the banner that announces a credit-limit suspension.
   *
   * That banner has to name the limit even when the limit is zero: a customer on payment terms
   * whose limit is zero really is over it, and hiding the number leaves "Booking suspended as
   * you are over your credit limit" with nothing to explain it. The zero-limit sentences are
   * dropped everywhere else because there they mean "no credit terms", not "a limit of nothing".
   *
   * It also changes what the open figure is called. This banner is handed
   * issuedInvoicesChargesTotal, which excludes refunds rather than subtracting them, so calling
   * it "open invoices" like the other banners would name the wrong quantity.
   */
  enforcingCreditLimit?: boolean;
}

/**
 * The credit banner's body text, for every state the banner renders in.
 *
 * One builder rather than one per state. The sentences differ only by the overdue line, and
 * keeping a second copy is how the open-invoices figure came to disagree with the totals beside
 * it in the first place (ENG-7104).
 *
 * A company with no credit limit has no credit terms, so the terms and limit sentences are
 * dropped rather than printed as zeroes. Telling a customer who pays per shipment by card that
 * their credit limit is $0 reads as a refusal instead of a description of their account, and most
 * of our customers pay that way. Note the backend sends 0 both for a deliberate zero and for an
 * unset limit (`creditLimit.orZero()`), so this can't tell those apart - they mean the same thing
 * to the customer either way. The one exception is `enforcingCreditLimit`; see that prop.
 */
// Keeps the spacing right when there is no currency code to print - a mixed-currency account
// would otherwise get a double space where the code would have gone.
function joinAmount(
  lead: string,
  amount: string,
  currency: string,
  tail: string,
): string {
  return [lead, amount, currency, tail].filter((part) => part !== "").join(" ");
}

export function invoicesGenerateCreditBannerMessage(
  props: CreditBannerMessageProps,
): string {
  const {
    paymentTermsDays,
    creditAmount,
    creditCurrency,
    issuedInvoicesTotal,
    invoicesCurrency,
    overdueIssuedInvoicesTotal,
    enforcingCreditLimit = false,
  } = props;

  // The limit is a property of the account, so it is in the company's currency. The invoice totals
  // are in whatever the invoices themselves are. Conflating the two is what printed "USD" on a CAD
  // balance.
  const limitCurrency = creditCurrency?.toUpperCase() ?? "";
  const amountCurrency = invoicesCurrency ?? "";
  const sentences: string[] = [];

  if (
    enforcingCreditLimit ||
    (creditAmount !== undefined && creditAmount > 0)
  ) {
    sentences.push(`Payment terms are ${paymentTermsDays} days.`);
    sentences.push(
      `Your credit limit is $${formatPrice(creditAmount ?? 0)} ${limitCurrency}.`,
    );
  }

  // Signed, because subtracting refunds means this can be negative when we owe the customer more
  // than they owe us - and a minus belongs in front of the dollar sign, not after it.
  sentences.push(
    joinAmount(
      "You currently have",
      formatSignedPrice(issuedInvoicesTotal ?? 0),
      amountCurrency,
      enforcingCreditLimit ? "in open charges." : "in open invoices.",
    ),
  );

  if (overdueIssuedInvoicesTotal !== undefined) {
    sentences.push(
      joinAmount(
        "There are",
        formatSignedPrice(overdueIssuedInvoicesTotal),
        amountCurrency,
        "in overdue invoices.",
      ),
    );
  }

  return sentences.join(" ");
}

/**
 * The banner's title. "Credit Terms" is a promise the section can't keep for a customer who
 * doesn't have any, so it describes the account instead.
 */
export function invoicesCreditBannerTitle(
  creditAmount: number | undefined,
): string {
  return creditAmount !== undefined && creditAmount > 0
    ? "Credit Terms"
    : "Account";
}
